import React from "react";
import {
  CheckCircle,
  XCircle,
  FilePdf,
  FileImage,
  FileDoc,
  Sparkle,
  CaretRight,
} from "@phosphor-icons/react";
import { useInView } from "./useInView";

/* ---------- Shared mock chrome ---------- */

function MockFrame({ children, testId }) {
  return (
    <div
      data-testid={testId}
      className="group relative border border-ink-200 bg-white rounded-sm overflow-hidden shadow-[0_24px_48px_-24px_rgba(15,23,42,0.18),0_4px_12px_-4px_rgba(15,23,42,0.08)] hover:shadow-[0_32px_64px_-24px_rgba(15,23,42,0.22),0_6px_16px_-4px_rgba(15,23,42,0.10)] hover:-translate-y-0.5 transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
    >
      <div
        className="absolute top-0 left-0 h-0.5 w-16 bg-brand-400 z-10"
        aria-hidden="true"
      />
      {/* On mobile we let the height grow naturally so nothing gets clipped.
          From sm: we lock the aspect ratio for the editorial look. */}
      <div className="px-4 py-5 sm:px-10 sm:py-10 flex flex-col bg-white sm:aspect-[1200/771]">
        {children}
      </div>
    </div>
  );
}

function MockHeader({ step, title }) {
  return (
    <div className="mb-3 sm:mb-6">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
        Schritt {step} von 4
      </div>
      <div className="font-cabinet text-base sm:text-lg font-medium tracking-tight text-ink-900 leading-tight">
        {title}
      </div>
    </div>
  );
}

/**
 * Animation wrapper — children appear with a small upward fade after a delay.
 * Uses inline transition for fine-grained, cascaded reveals.
 */
function Reveal({ inView, delay = 0, children, className = "" }) {
  return (
    <div
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(8px)",
        transition: `opacity 520ms cubic-bezier(0.2, 0.8, 0.2, 1) ${delay}ms, transform 520ms cubic-bezier(0.2, 0.8, 0.2, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ---------- Step 1: KV-Auswahl ---------- */

const MOCK_KVS = [
  { kuerzel: "HD", name: "KV Handel", active: true },
  { kuerzel: "MV", name: "KV Metall" },
  { kuerzel: "IT", name: "KV IT" },
  { kuerzel: "GH", name: "KV Gewerbe" },
  { kuerzel: "BK", name: "KV Banken" },
  { kuerzel: "TV", name: "KV Tourismus" },
];

export function StepOneMock() {
  const { ref, inView } = useInView();
  return (
    <div ref={ref}>
      <MockFrame testId="landing-mock-step1">
        <MockHeader step="1" title="Kollektivvertrag & Stichtag wählen" />

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5 mb-4 sm:mb-5">
          {MOCK_KVS.map((kv, i) => (
            <Reveal key={kv.kuerzel} inView={inView} delay={120 + i * 70}>
              <div
                className={[
                  "rounded-sm p-2.5 flex items-center gap-2 border bg-white",
                  kv.active
                    ? "border-2 border-brand-400 bg-brand-50"
                    : "border-ink-200",
                ].join(" ")}
              >
                <span
                  className={[
                    "shrink-0 inline-flex items-center justify-center rounded-sm font-cabinet text-[10px] font-black tracking-wide px-1.5 min-w-[2rem] h-6 uppercase border",
                    kv.active
                      ? "border-brand-500 bg-brand-100 text-ink-900"
                      : "border-brand-300 bg-brand-50 text-ink-900",
                  ].join(" ")}
                  aria-hidden="true"
                >
                  {kv.kuerzel}
                </span>
                <div className="text-[11px] text-ink-900 truncate">
                  {kv.name}
                </div>
                {kv.active && (
                  <CheckCircle
                    size={12}
                    weight="fill"
                    className="text-brand-600 ml-auto shrink-0"
                  />
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal inView={inView} delay={600}>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1.5">
            Stichtag der Einstufung
          </div>
          <div className="inline-flex items-center gap-2 rounded-sm border border-ink-300 bg-white px-3 py-1.5 text-xs text-ink-900">
            05.05.2026
            <span className="text-warm-400">·</span>
            <span className="text-warm-500">heute</span>
          </div>
        </Reveal>
      </MockFrame>
    </div>
  );
}

/* ---------- Step 2: KI-Analyse ---------- */

export function StepTwoMock() {
  const { ref, inView } = useInView();
  return (
    <div ref={ref}>
      <MockFrame testId="landing-mock-step2">
        <MockHeader step="2" title="Tätigkeit beschreiben & Analyse" />

        {/* Documents row */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4 sm:mb-5">
          {[
            { icon: FilePdf, name: "Stellenbeschreibung.pdf" },
            { icon: FileImage, name: "Organigramm.png" },
            { icon: FileDoc, name: "Dienstvertrag.docx" },
          ].map(({ icon: Icon, name }, i) => (
            <Reveal key={name} inView={inView} delay={120 + i * 90}>
              <div className="inline-flex items-center gap-1.5 rounded-sm border border-ink-200 bg-white px-2 py-1.5 text-[10px] text-ink-900">
                <Icon size={12} weight="duotone" className="text-brand-500" />
                {name}
              </div>
            </Reveal>
          ))}
        </div>

        {/* AI scanning indicator */}
        <Reveal inView={inView} delay={520}>
          <div className="rounded-sm border border-dashed border-brand-300 bg-brand-50 px-3 py-2.5 mb-4 flex items-center gap-2.5">
            <Sparkle
              size={14}
              weight="fill"
              className="text-brand-500"
              style={{
                animation: inView ? "spin 4s linear infinite" : "none",
              }}
            />
            <div className="flex-1 text-[11px] text-ink-900 font-medium">
              ET analysiert Tätigkeit & Dokumente…
            </div>
            <div className="text-[10px] text-warm-500 font-mono">2/2</div>
          </div>
        </Reveal>

        {/* Result card */}
        <Reveal inView={inView} delay={820}>
          <div className="rounded-sm border-2 border-brand-400 bg-white p-3.5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                Vorschlag
              </div>
              <div className="text-[10px] font-mono text-brand-700">
                Konfidenz 87%
              </div>
            </div>
            <div className="flex items-baseline gap-3">
              <div className="font-cabinet text-3xl font-medium text-brand-700 leading-none">
                D
              </div>
              <div className="text-[12px] text-warm-700 leading-snug">
                Mitarbeiter mit überwiegend selbständig ausgeführten qualifizierten Arbeiten unter allgemeiner Anleitung.
              </div>
            </div>
            <div className="mt-3 h-1 w-full bg-warm-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-500"
                style={{
                  width: inView ? "87%" : "0%",
                  transition: "width 900ms cubic-bezier(0.2,0.8,0.2,1) 900ms",
                }}
              />
            </div>
          </div>
        </Reveal>
      </MockFrame>
    </div>
  );
}

/* ---------- Step 3: Vordienstzeiten ---------- */

const MOCK_VORDIENST = [
  {
    title: "Lagerist · Müller GmbH",
    dauer: "2 J · 6 M",
    angerechnet: true,
  },
  {
    title: "Verkäufer · Spar",
    dauer: "1 J · 0 M",
    angerechnet: true,
  },
  {
    title: "Praktikum (Schule)",
    dauer: "0 J · 3 M",
    angerechnet: false,
  },
];

export function StepThreeMock() {
  const { ref, inView } = useInView();
  return (
    <div ref={ref}>
      <MockFrame testId="landing-mock-step3">
        <MockHeader step="3" title="Vordienstzeiten erfassen" />

        {/* Mock table */}
        <div className="rounded-sm border border-ink-200 bg-white overflow-hidden divide-y divide-ink-200">
          {MOCK_VORDIENST.map((row, i) => (
            <Reveal key={row.title} inView={inView} delay={120 + i * 130}>
              <div className="flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 sm:py-2.5">
                {row.angerechnet ? (
                  <CheckCircle
                    size={16}
                    weight="fill"
                    className="text-brand-600 shrink-0"
                  />
                ) : (
                  <XCircle
                    size={16}
                    weight="fill"
                    className="text-warm-400 shrink-0"
                  />
                )}
                <div className="flex-1 text-[11px] sm:text-[12px] text-ink-900 truncate min-w-0">
                  {row.title}
                </div>
                <div className="text-[10px] sm:text-[11px] font-mono text-warm-600 tabular-nums shrink-0">
                  {row.dauer}
                </div>
                <div
                  className={[
                    "hidden sm:inline text-[10px] uppercase tracking-wider font-semibold shrink-0",
                    row.angerechnet ? "text-brand-700" : "text-warm-500",
                  ].join(" ")}
                >
                  {row.angerechnet ? "angerechnet" : "nicht angerechnet"}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Sum row */}
        <Reveal inView={inView} delay={600}>
          <div className="mt-4 flex items-center justify-between rounded-sm bg-ink-900 text-white px-3.5 py-2.5">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-warm-300">
              Anrechenbar gesamt
            </div>
            <div className="font-cabinet text-base font-medium leading-none">
              3<span className="text-[10px] uppercase tracking-widest text-warm-400 ml-1.5">Jahre</span>
              <span className="mx-2 text-brand-500">·</span>
              6<span className="text-[10px] uppercase tracking-widest text-warm-400 ml-1.5">Monate</span>
            </div>
          </div>
        </Reveal>
      </MockFrame>
    </div>
  );
}

/* ---------- Step 4: Empfehlung ---------- */

function CountUp({ to, inView, delay = 0, suffix = "" }) {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    if (!inView) return undefined;
    const start = performance.now() + delay;
    const dur = 700;
    let raf;
    const tick = (now) => {
      if (now < start) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, delay]);
  return (
    <span>
      {n}
      {suffix}
    </span>
  );
}

export function StepFourMock() {
  const { ref, inView } = useInView();
  return (
    <div ref={ref}>
      <MockFrame testId="landing-mock-step4">
        <MockHeader step="4" title="Finale Einstufung & PDF-Export" />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 flex-1">
          {/* Hero — Gruppe + Stufe (mirrors Step 4 result tile) */}
          <Reveal inView={inView} delay={120} className="sm:col-span-2">
            <div className="relative h-full overflow-hidden bg-warm-50 border border-ink-200 rounded-sm flex">
              <div
                className="absolute top-0 left-0 h-[3px] w-full bg-brand-500"
                aria-hidden="true"
              />
              <div className="flex-1 grid grid-cols-2 divide-x divide-ink-200">
                <div className="px-3 sm:px-4 py-3 sm:py-4 flex flex-col">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1.5">
                    Gruppe
                  </div>
                  <div className="font-cabinet font-medium text-[44px] sm:text-[56px] leading-none tracking-tight text-brand-700">
                    D
                  </div>
                </div>
                <div className="px-3 sm:px-4 py-3 sm:py-4 flex flex-col">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1.5">
                    Stufe
                  </div>
                  <div className="font-cabinet font-medium text-[44px] sm:text-[56px] leading-none tracking-tight text-brand-500">
                    3
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Side card — Anrechenbare Dienstzeit */}
          <Reveal inView={inView} delay={320}>
            <div className="h-full border border-ink-200 rounded-sm bg-white px-4 py-4 flex flex-col">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
                Anrechenbare Zeit
              </div>
              <div className="flex-1 flex items-center">
                <div className="space-y-1">
                  {[
                    [3, "Jahre"],
                    [6, "Monate"],
                    [22, "Tage"],
                  ].map(([val, unit], i) => (
                    <div key={unit} className="flex items-baseline gap-1.5">
                      <span className="font-cabinet font-medium text-xl leading-none text-ink-900 tabular-nums w-[2ch] text-right">
                        <CountUp to={val} inView={inView} delay={400 + i * 120} />
                      </span>
                      <span className="text-[9px] uppercase tracking-widest font-semibold text-warm-500">
                        {unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* PDF download chip */}
        <Reveal inView={inView} delay={900}>
          <div className="mt-4 inline-flex items-center gap-2 rounded-sm border border-ink-300 bg-white px-3 py-1.5 text-[11px] text-ink-900 self-start">
            <FilePdf size={12} weight="duotone" className="text-brand-500" />
            Empfehlung_KV-Handel.pdf erstellt
            <CaretRight size={10} weight="bold" className="text-warm-500" />
          </div>
        </Reveal>
      </MockFrame>
    </div>
  );
}
